import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { Trans } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { AlertTriangle, RotateCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { VaultUnlockCancelledError } from "@/lib/hooks/use-protected-action";
import {
  clearVaultUnlockHint,
  getVaultUnlockExpiry,
  isVaultLockedError,
  lockVault,
  markVaultUnlocked,
  reportVaultLocked,
} from "@/lib/vault-unlock";

import { PinGateModal } from "./pin-gate-modal";

// The PIN gate in front of a protected area (`feature`: "medical_vault",
// "wallet", "billing").
//
// The server decides (2026-10): the medical-vault endpoints refuse a locked
// account with 403 `vault_locked`, and GET /api/account/vault-security says
// whether this browser holds a live unlock. So:
//   - the area opens only when the account does not protect it, or the server
//     reports an unlock (the tab's sessionStorage hint stands in only for an
//     API that does not report one yet);
//   - a failed status read shows an error with a retry — never the content
//     (it used to fail OPEN);
//   - "vault-locked" (lib/vault-unlock.ts — a vault call answered
//     vault_locked, the Lock action, another gate's timer) brings the gate
//     back; the content unmounts, so it refetches once unlocked again;
//   - when the unlock runs out here (its end is kept in state as well as in
//     the tab's hint), POST /lock ends the server's unlock too;
//   - a write inside the area goes through useVaultLock().runUnlocked(call):
//     when the API answers vault_locked the gate comes back, and once the PIN
//     (or a vault passkey) is accepted the call runs ONE more time — its
//     promise settles even though the content unmounted meanwhile, so keep
//     its follow-up in mutation-level callbacks / the query cache. Dismissing
//     the gate rejects it with VaultUnlockCancelledError.

interface SecurityData {
  method: string;
  hasPin: boolean;
  pinLength: number;
  unlockMinutes: number;
  protectedFeatures: string[];
  /** Does this request carry a live server unlock? Absent on an API older than 2026-10. */
  unlocked?: boolean;
  unlockExpiresInSeconds?: number | null;
}

const fetchStatus = () => apiFetch.get<SecurityData>("/api/account/vault-security");

/**
 * The status read, through react-query (P32). A route loader can prefetch it
 * in parallel with the consent read (`prefetchVaultGateStatus`); the gate
 * then takes that copy only while it is a few seconds old and drops it from
 * the cache once used, so the open/locked decision never rests on a stale
 * status. Every later re-read goes to the server.
 */
const VAULT_GATE_STATUS_KEY = ["api", "/api/account/vault-security", "gate"] as const;
const PREFETCH_FRESH_MS = 10_000;

export function prefetchVaultGateStatus(queryClient: QueryClient): Promise<void> {
  return queryClient.prefetchQuery({
    queryKey: VAULT_GATE_STATUS_KEY,
    queryFn: fetchStatus,
    staleTime: PREFETCH_FRESH_MS,
  });
}

async function readStatus(queryClient: QueryClient, allowPrefetched: boolean) {
  try {
    return await queryClient.fetchQuery({
      queryKey: VAULT_GATE_STATUS_KEY,
      queryFn: fetchStatus,
      staleTime: allowPrefetched ? PREFETCH_FRESH_MS : 0,
    });
  } finally {
    // One-shot hand-off: never reuse a status for a later decision.
    queryClient.removeQueries({ queryKey: VAULT_GATE_STATUS_KEY, exact: true });
  }
}

/** The server's rule (apps/api lib/vault-unlock.ts vaultProtects). */
function protects(data: SecurityData, feature: string): boolean {
  // `sign_in` is a virtual entry the status adds (passkey sign-in), not a stored area.
  const features = (data.protectedFeatures || []).filter((f) => f !== "sign_in");
  const effective = features.length === 0 && data.hasPin ? ["medical_vault"] : features;
  return data.method !== "none" && data.hasPin && effective.includes(feature);
}

interface VaultLockState {
  /** The area is protected and open: locking it now means something. */
  canLock: boolean;
  lock: () => void;
  /** Run an API call of this area; on vault_locked, unlock and run it once more. */
  runUnlocked: <T>(call: () => Promise<T>) => Promise<T>;
}

const VaultLockContext = createContext<VaultLockState>({
  canLock: false,
  lock: () => {},
  runUnlocked: (call) => call(),
});

/** For a "Lock" action, or a write, inside a ProtectedContent. */
export function useVaultLock(): VaultLockState {
  return useContext(VaultLockContext);
}

interface ProtectedContentProps {
  feature: string;
  children: React.ReactNode;
  /**
   * Shown while the status loads and while the area is locked (under the PIN
   * sheet) — a skeleton of the page instead of a blank box. Defaults to a
   * small placeholder.
   */
  fallback?: ReactNode;
}

type Phase = "loading" | "error" | "ready";

export function ProtectedContent({ feature, children, fallback }: ProtectedContentProps) {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>("loading");
  const [securityData, setSecurityData] = useState<SecurityData | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  /** When the open area's unlock ends (ms since epoch); null = not protected, or locked. */
  const [unlockUntil, setUnlockUntil] = useState<number | null>(null);
  const [showGate, setShowGate] = useState(false);
  /** A call the API refused with vault_locked, waiting for the unlock to run again. */
  const pendingRef = useRef<{ proceed: () => void; cancel: () => void } | null>(null);
  const mountedRef = useRef(true);

  /** The area is open again: run what waited for it. */
  const flushPending = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.proceed();
  }, []);

  const lockHere = useCallback(() => {
    // A status prefetched before this lock (route hover) must not reopen the
    // area on the next mount: mark it stale (no removal — that would cancel a
    // read in flight and show the error card).
    void queryClient.invalidateQueries({
      queryKey: VAULT_GATE_STATUS_KEY,
      exact: true,
      refetchType: "none",
    });
    setUnlocked(false);
    setUnlockUntil(null);
    setShowGate(true);
  }, [queryClient]);

  /** Open or lock the area from the protection status the server reported. */
  const applyStatus = useCallback(
    (data: SecurityData) => {
      setSecurityData(data);
      // The server's word on this browser's unlock; an API that does not
      // report it yet leaves the tab's hint to stand in.
      const until =
        data.unlocked === undefined
          ? getVaultUnlockExpiry()
          : data.unlocked
            ? markVaultUnlocked(data.unlockMinutes, data.unlockExpiresInSeconds)
            : null;
      if (!protects(data, feature)) {
        setUnlocked(true);
        setUnlockUntil(null);
        setShowGate(false);
        flushPending();
      } else if (until !== null) {
        setUnlocked(true);
        setUnlockUntil(until);
        setShowGate(false);
        flushPending();
      } else {
        // A hint the server contradicts is dropped.
        clearVaultUnlockHint();
        lockHere();
      }
      setPhase("ready");
    },
    [feature, lockHere, flushPending],
  );

  /** The status could not be read: closed — whether the area is protected is unknown. */
  const failStatus = useCallback(() => {
    setUnlocked(false);
    setUnlockUntil(null);
    setShowGate(false);
    setPhase("error");
  }, []);

  /** Re-read the status. `silent` keeps the current view while it loads. */
  const reload = useCallback(
    (opts: { silent?: boolean } = {}) => {
      if (!opts.silent) setPhase("loading");
      return readStatus(queryClient, false).then(applyStatus, failStatus);
    },
    [applyStatus, failStatus, queryClient],
  );

  useEffect(() => {
    // First read: a status the route prefetched moments ago is accepted.
    void readStatus(queryClient, true).then(applyStatus, failStatus);
  }, [applyStatus, failStatus, queryClient]);

  // Unmounted (navigated away, or the gate dismissed): what waited is given up.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const pending = pendingRef.current;
      pendingRef.current = null;
      pending?.cancel();
    };
  }, []);

  const runUnlocked = useCallback(
    <T,>(call: () => Promise<T>): Promise<T> =>
      call().catch((err: unknown) => {
        if (!isVaultLockedError(err)) throw err;
        if (!mountedRef.current) throw new VaultUnlockCancelledError();
        return new Promise<T>((resolve, reject) => {
          const previous = pendingRef.current;
          pendingRef.current = {
            proceed: () => {
              call().then(resolve, reject);
            },
            cancel: () => reject(new VaultUnlockCancelledError()),
          };
          previous?.cancel();
          // The server holds no unlock for this browser: drop the tab's hint
          // and bring every gate back — this one at once (even on a stale
          // "not protected" status), then re-read the status for the PIN
          // length; a status that says the area is open runs the call again.
          reportVaultLocked();
          lockHere();
          void reload({ silent: true });
        });
      }),
    [lockHere, reload],
  );

  const isProtected = securityData ? protects(securityData, feature) : false;

  // The unlock ended elsewhere — the API answered vault_locked, the Lock
  // action, another gate's timer.
  useEffect(() => {
    const onLocked = () => {
      if (!securityData || !protects(securityData, feature)) {
        // Protection may have been switched on since the status was read.
        void reload({ silent: true });
        return;
      }
      lockHere();
    };
    window.addEventListener("vault-locked", onLocked);
    return () => window.removeEventListener("vault-locked", onLocked);
  }, [securityData, feature, reload, lockHere]);

  // The unlock runs out here as it does on the server: lock both.
  useEffect(() => {
    if (!unlocked || !isProtected || unlockUntil === null) return;
    const check = () => {
      if (Date.now() >= unlockUntil) {
        lockHere();
        void lockVault();
      }
    };
    const interval = setInterval(check, 5_000);
    return () => clearInterval(interval);
  }, [unlocked, isProtected, unlockUntil, lockHere]);

  // The PIN or a vault passkey was just accepted — the server wrote pm_vault.
  const handleUnlock = useCallback(() => {
    setUnlockUntil(markVaultUnlocked(securityData?.unlockMinutes));
    setUnlocked(true);
    setShowGate(false);
    flushPending();
  }, [securityData?.unlockMinutes, flushPending]);

  const handleCancel = useCallback(() => {
    setShowGate(false);
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.cancel();
    if (typeof window !== "undefined") {
      window.history.back();
    }
  }, []);

  const lockState = useMemo<VaultLockState>(
    () => ({
      canLock: isProtected && unlocked,
      lock: () => {
        lockHere();
        void lockVault();
      },
      runUnlocked,
    }),
    [isProtected, unlocked, lockHere, runUnlocked],
  );

  const placeholder = fallback ?? (
    <div className="flex min-h-[400px] items-center justify-center">
      <div className="bg-muted size-8 animate-pulse rounded-full" />
    </div>
  );

  if (phase === "loading") return <>{placeholder}</>;

  if (phase === "error") {
    return (
      <div className="flex min-h-[400px] items-center justify-center px-4">
        <div className="border-warning-border bg-warning-soft w-full max-w-sm rounded-xl border p-6 text-center">
          <AlertTriangle className="text-warning mx-auto size-6" />
          <p className="text-warning-strong mt-3 text-sm font-medium">
            <Trans>We couldn't check your vault protection.</Trans>
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            <Trans>Check your connection, then try again.</Trans>
          </p>
          <Button
            variant="outline"
            className="mt-4 min-h-11 gap-2"
            onClick={() => {
              void reload();
            }}
          >
            <RotateCw className="size-4" />
            <Trans>Try again</Trans>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <VaultLockContext.Provider value={lockState}>
      {unlocked ? children : placeholder}
      <PinGateModal
        open={showGate}
        onUnlock={handleUnlock}
        onCancel={handleCancel}
        pinLength={securityData?.pinLength || 0}
        hasBiometric={securityData?.method === "biometric"}
      />
    </VaultLockContext.Provider>
  );
}
